import { useEffect } from 'react';

/**
 * Comprehensive SEO hook — updates document title, meta description, canonical,
 * Open Graph, Twitter Card, and robots meta on every route change.
 * Optimized for classical SEO (Google/Bing), AEO (featured snippets, AI Overviews),
 * and LLMO (ChatGPT, Claude, Gemini, Perplexity citations).
 */
const SITE = {
  name: 'Ransen',
  url: 'https://ransen.io',
  twitter: '@ransen',
  defaultOgImage: 'https://ransen.io/favicon.png',
};

function setMeta({ name, property, content }) {
  if (!content) return;
  const attr = name ? 'name' : 'property';
  const key = name || property;
  const selector = `meta[${attr}="${key}"]`;
  let tag = document.head.querySelector(selector);
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute(attr, key);
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', content);
}

function setLink({ rel, href }) {
  if (!href) return;
  let link = document.head.querySelector(`link[rel="${rel}"]`);
  if (!link) {
    link = document.createElement('link');
    link.setAttribute('rel', rel);
    document.head.appendChild(link);
  }
  link.setAttribute('href', href);
}

export default function useSEO({
  title,
  description,
  canonical,
  ogImage,
  ogType = 'website',
  keywords,
  robots = 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1',
  author = 'Ransen',
} = {}) {
  useEffect(() => {
    if (title) document.title = title;

    setMeta({ name: 'description', content: description });
    setMeta({ name: 'keywords', content: keywords });
    setMeta({ name: 'robots', content: robots });
    setMeta({ name: 'author', content: author });

    // Open Graph
    setMeta({ property: 'og:site_name', content: SITE.name });
    setMeta({ property: 'og:type', content: ogType });
    setMeta({ property: 'og:title', content: title });
    setMeta({ property: 'og:description', content: description });
    setMeta({ property: 'og:url', content: canonical });
    setMeta({ property: 'og:image', content: ogImage || SITE.defaultOgImage });
    setMeta({ property: 'og:image:width', content: '1200' });
    setMeta({ property: 'og:image:height', content: '630' });
    setMeta({ property: 'og:locale', content: 'en_US' });

    // Twitter card
    setMeta({ name: 'twitter:card', content: 'summary_large_image' });
    setMeta({ name: 'twitter:site', content: SITE.twitter });
    setMeta({ name: 'twitter:creator', content: SITE.twitter });
    setMeta({ name: 'twitter:title', content: title });
    setMeta({ name: 'twitter:description', content: description });
    setMeta({ name: 'twitter:image', content: ogImage || SITE.defaultOgImage });

    // Canonical
    setLink({ rel: 'canonical', href: canonical });
  }, [title, description, canonical, ogImage, ogType, keywords, robots, author]);
}
